import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import DayPicker, { DayPickerProps } from 'react-day-picker';
import MomentLocaleUtils from 'react-day-picker/moment';

const WEEKDAY_NAMES_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function toNativeDate(value: any): Date {
  if (value instanceof Date) return value;
  if (value && typeof value.toDate === 'function') return value.toDate();
  return new Date(value);
}

export interface ScrollablePickerProps {
  fromMonth: Date;
  toMonth: Date;
  selectedDate?: Date;
  initialMonth?: Date;
  disabledDays?: DayPickerProps['disabledDays'];
  visible?: boolean;
  onSelect: (date: Date) => void;
}

const ScrollableMonthPicker: React.FC<ScrollablePickerProps> = ({
  fromMonth,
  toMonth,
  selectedDate,
  initialMonth,
  disabledDays,
  visible,
  onSelect,
}) => {
  const bodyRef = useRef<HTMLDivElement>(null);

  const numMonths = useMemo(() => {
    const from = moment(fromMonth).startOf('month');
    const to = moment(toMonth).startOf('month');
    const diff = to.diff(from, 'months') + 1;
    return Math.max(1, diff);
  }, [fromMonth, toMonth]);

  const monthNames: string[] = MomentLocaleUtils.getMonths(moment.locale());

  const weekdayNames = useMemo(() => {
    const locale = moment.locale();
    if (locale === 'en' || locale.startsWith('en-')) return WEEKDAY_NAMES_SHORT;
    return Array.from({ length: 7 }, (_, i) =>
      moment()
        .locale(locale)
        .day(i)
        .format('ddd')
    );
  }, []);

  const years = useMemo(() => {
    const result: number[] = [];
    for (let y = fromMonth.getFullYear(); y <= toMonth.getFullYear(); y++) {
      result.push(y);
    }
    return result;
  }, [fromMonth, toMonth]);

  const initialDate = useMemo(() => {
    const raw = selectedDate ?? initialMonth ?? fromMonth;
    return toNativeDate(raw);
  }, [selectedDate, initialMonth, fromMonth]);
  const [navYear, setNavYear] = useState(initialDate.getFullYear());
  const [navMonth, setNavMonth] = useState(initialDate.getMonth());

  useEffect(() => {
    if (!visible) return;
    const targetDate = toNativeDate(selectedDate ?? initialMonth ?? fromMonth);
    setNavYear(targetDate.getFullYear());
    setNavMonth(targetDate.getMonth());
    requestAnimationFrame(() => {
      const monthIndex =
        (targetDate.getFullYear() - fromMonth.getFullYear()) * 12 +
        targetDate.getMonth() -
        fromMonth.getMonth();
      const els = bodyRef.current?.querySelectorAll('.DayPicker-Month');
      if (els?.[monthIndex]) {
        (els[monthIndex] as HTMLElement).scrollIntoView({ block: 'nearest' });
      }
    });
  }, [visible, initialMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  const availableMonths = useMemo(() => {
    const from = new Date(fromMonth.getFullYear(), fromMonth.getMonth(), 1);
    const to = new Date(toMonth.getFullYear(), toMonth.getMonth(), 1);
    return monthNames
      .map((name, index) => ({ name, index }))
      .filter(({ index }) => {
        const d = new Date(navYear, index, 1);
        return d >= from && d <= to;
      });
  }, [monthNames, navYear, fromMonth, toMonth]);

  const scrollToMonth = (year: number, month: number) => {
    const monthIndex = (year - fromMonth.getFullYear()) * 12 + month - fromMonth.getMonth();
    const els = bodyRef.current?.querySelectorAll('.DayPicker-Month');
    if (els?.[monthIndex]) {
      (els[monthIndex] as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const year = Number(e.target.value);
    setNavYear(year);
    const firstAvail = year === fromMonth.getFullYear() ? fromMonth.getMonth() : 0;
    const lastAvail = year === toMonth.getFullYear() ? toMonth.getMonth() : 11;
    const newMonth = Math.min(Math.max(navMonth, firstAvail), lastAvail);
    setNavMonth(newMonth);
    scrollToMonth(year, newMonth);
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const month = Number(e.target.value);
    setNavMonth(month);
    scrollToMonth(navYear, month);
  };

  return (
    <div className="sp-picker">
      <div className="sp-picker__header">
        <div className="sp-picker__nav" onClick={e => e.stopPropagation()}>
          <div className="sp-picker__select-wrap">
            <select className="sp-picker__select" value={navMonth} onChange={handleMonthChange}>
              {availableMonths.map(({ name, index }) => (
                <option key={index} value={index}>
                  {name}
                </option>
              ))}
            </select>
          </div>
          <div className="sp-picker__select-wrap">
            <select className="sp-picker__select" value={navYear} onChange={handleYearChange}>
              {years.map(y => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="sp-picker__weekdays">
          {weekdayNames.map((day, i) => (
            <span key={i} className="sp-picker__weekday">
              {day}
            </span>
          ))}
        </div>
      </div>

      <div className="sp-picker__body" ref={bodyRef}>
        <DayPicker
          month={fromMonth}
          fromMonth={fromMonth}
          toMonth={toMonth}
          numberOfMonths={numMonths}
          canChangeMonth={false}
          selectedDays={selectedDate ? toNativeDate(selectedDate) : undefined}
          disabledDays={disabledDays}
          locale={moment.locale()}
          localeUtils={MomentLocaleUtils}
          onDayClick={(day, modifiers) => {
            if (modifiers.disabled || modifiers.outside) return;
            onSelect(day);
          }}
        />
      </div>

      <style jsx global>{`
        .sp-picker .DayPicker {
          width: 100%;
        }
        .sp-picker .DayPicker-wrapper {
          padding: 0;
        }
        .sp-picker .DayPicker-Months {
          flex-direction: column;
          width: 100%;
        }
        .sp-picker .DayPicker-Month {
          margin: 0;
          width: 100%;
        }
        .sp-picker .DayPicker-NavButton {
          display: none;
        }
        .sp-picker .DayPicker-Caption > div {
          font-size: 14px;
          color: var(--color-purple_6);
          font-family: 'UniversalSans';
          padding: 8px 4px 2px;
          font-weight: 900;
        }
        .sp-picker .DayPicker-Weekdays {
          display: none;
        }
        .sp-picker .DayPicker-Weekday {
          color: var(--color-purple_4);
          font-size: 13px;
          padding: 8px 0;
        }
        .sp-picker .DayPicker-Week {
          display: flex;
        }
        .sp-picker .DayPicker-Day {
          flex: 1;
          aspect-ratio: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          padding: 0;
          color: var(--color-purple_6);
        }
        .sp-picker .DayPicker-Day--selected:not(.DayPicker-Day--outside) {
          background: var(--color-purple_6) !important;
          color: var(--color-white) !important;
          border-radius: 50% !important;
        }
        .sp-picker
          .DayPicker:not(.DayPicker--interactionDisabled)
          .DayPicker-Day:not(.DayPicker-Day--disabled):not(.DayPicker-Day--selected):not(.DayPicker-Day--outside):hover {
          background: var(--color-purple_1);
          border-radius: 50%;
        }
        .sp-picker .DayPicker-Day--disabled {
          color: var(--color-gray_6);
        }
        .sp-picker .DayPicker-Day--outside {
          color: var(--color-purple_3);
        }
      `}</style>

      <style jsx>{`
        .sp-picker {
          width: 348px;
          background: var(--color-white);
          border-radius: 14px;
          overflow: hidden;
          box-shadow: -2px -2px 8px 0 rgba(0, 0, 0, 0.05), 2px 2px 8px 0 rgba(0, 0, 0, 0.05);
        }
        .sp-picker__nav {
          display: flex;
          flex-direction: row;
          justify-content: center;
          align-items: center;
          gap: 4px;
          padding: 14px 16px 8px;
          background: var(--color-white);
        }
        .sp-picker__select-wrap {
          position: relative;
        }
        .sp-picker__select-wrap:after {
          position: absolute;
          top: 50%;
          right: 6px;
          transform: translateY(-25%);
          border-top: 6px solid var(--color-purple_4);
          border-right: 4px solid transparent;
          border-left: 4px solid transparent;
          content: '';
          pointer-events: none;
        }
        .sp-picker__select {
          font-size: 16px;
          color: var(--color-purple_6);
          padding: 2px 24px 2px 4px;
          border: none;
          outline: none;
          background-color: transparent;
          appearance: none;
          cursor: pointer;
          font-family: 'UniversalSans';
        }
        .sp-picker__header {
          position: sticky;
          top: 0;
          z-index: 1;
          background: var(--color-white);
        }
        .sp-picker__weekdays {
          display: flex;
          padding: 8px 12px 10px;
          border-bottom: 1px solid var(--color-purple_2);
        }
        .sp-picker__weekday {
          flex: 1;
          text-align: center;
          font-size: 13px;
          color: var(--color-purple_4);
          font-family: 'UniversalSans';
        }
        .sp-picker__body {
          overflow-y: auto;
          max-height: 310px;
          padding: 0 8px;
          border-radius: 0 0 14px 14px;
        }
      `}</style>
    </div>
  );
};

export default ScrollableMonthPicker;
